/**
 * Simple Mixpanel integration using native features
 */
import mixpanel from 'mixpanel-browser';

// Track recently viewed pages to prevent duplicates
const recentPageViews = {};
const recentFormStarts = {};

// Initialize with your project token
mixpanel.init('305793527dce66e70f86538c2439fba8', {
  debug: process.env.NODE_ENV === 'development',
  track_pageview: false, // Disable native tracking to prevent duplicates
  persistence: 'localStorage',
  api_host: 'https://api-js.mixpanel.com',
  ignore_dnt: true,
  batch_requests: true,
  property_blacklist: ['$current_url', '$initial_referrer']
});

// Register common properties that will be included with all events
mixpanel.register({
  app_version: process.env.REACT_APP_VERSION || '1.0',
  platform: 'web'
});

/**
 * Check if a form start event was recently tracked
 * @param {string} formName - The name of the form
 * @returns {boolean} - True if the form was recently started
 */
const wasFormRecentlyStarted = (formName) => {
  const now = Date.now();
  const lastTracked = recentFormStarts[formName];
  
  if (lastTracked && (now - lastTracked) < 10000) {
    // Prevent duplicate form start events within 10 seconds
    return true;
  }
  
  // Update the timestamp
  recentFormStarts[formName] = now;
  return false;
};

/**
 * Check if a page was recently viewed
 * @param {string} path - The page path
 * @returns {boolean} - True if the page was recently viewed
 */
const wasPageRecentlyViewed = (path) => {
  const now = Date.now();
  const lastViewed = recentPageViews[path];
  
  if (lastViewed && (now - lastViewed) < 3000) {
    // Prevent duplicate page view events within 3 seconds
    return true;
  }
  
  // Update the timestamp
  recentPageViews[path] = now;
  return false;
};

// Simple helper functions with minimal overhead
const Mixpanel = {
  identify: (id) => {
    try {
      mixpanel.identify(id);
    } catch (error) {
      console.error('Mixpanel identify error:', error);
    }
  },
  
  // Core event tracking
  track: (name, props = {}) => {
    try {
      // Special handling for common duplicate events
      if (name === 'Page View') {
        const path = props.path || window.location.pathname;
        if (wasPageRecentlyViewed(path)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Prevented duplicate page view: ${path}`);
          }
          return;
        }
      }
      
      // Special handling for form start events
      if (name.includes('Form Started') || name.includes('Started')) {
        if (wasFormRecentlyStarted(name)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Prevented duplicate form start: ${name}`);
          }
          return;
        }
      }
      
      mixpanel.track(name, props);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Tracked event: ${name}`, props);
      }
    } catch (error) {
      console.error('Mixpanel track error:', error);
    }
  },
  
  // Track page views - with built-in duplicate prevention
  trackPageView: (pageName) => {
    try {
      const path = window.location.pathname;
      
      // Skip if this page was recently viewed
      if (wasPageRecentlyViewed(path)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Prevented duplicate page view: ${path}`);
        }
        return;
      }
      
      // Track the page view
      mixpanel.track('Page View', { 
        page: pageName || document.title,
        path: path,
        referrer: document.referrer
      });
    } catch (error) {
      console.error('Mixpanel page view error:', error);
    }
  },
  
  // Track form start events with duplicate prevention
  trackFormStart: (formName, props = {}) => {
    try {
      // Skip if this form was recently started
      if (wasFormRecentlyStarted(formName)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Prevented duplicate form start: ${formName}`);
        }
        return;
      }
      
      // Track the form start
      mixpanel.track(`${formName} Started`, props);
    } catch (error) {
      console.error(`Mixpanel form start error (${formName}):`, error);
    }
  },
  
  setUserProperty: (prop, value) => {
    try {
      mixpanel.people.set({ [prop]: value });
    } catch (error) {
      console.error('Mixpanel set user property error:', error);
    }
  },
  
  setUserProperties: (props) => {
    try {
      mixpanel.people.set(props);
    } catch (error) {
      console.error('Mixpanel set user properties error:', error);
    }
  },
  
  // For key core conversions
  trackConversion: (name, value = 0, props = {}) => {
    try {
      // Track the conversion event with properties
      mixpanel.track(name, props);
      
      // Track as a revenue event if value is provided
      if (value > 0) {
        mixpanel.people.track_charge(value, {
          ...props,
          event_name: name
        });
      }
    } catch (error) {
      console.error('Mixpanel conversion error:', error);
    }
  },
  
  // Payment flow specific tracking
  trackPaymentStart: (amount, paymentType, occasionType) => {
    try {
      // Skip if payment was recently started
      if (wasFormRecentlyStarted('Payment')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Prevented duplicate payment start');
        }
        return;
      }
      
      mixpanel.track('Payment Started', {
        amount,
        payment_type: paymentType,
        occasion_type: occasionType,
        funnel_step: 'payment_initiated'
      });
      
      // Set funnel stage property for cohort analysis
      mixpanel.people.set({
        payment_funnel_stage: 'started',
        last_payment_attempt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Mixpanel payment start error:', error);
    }
  },
  
  trackPaymentCompleted: (amount, paymentType, transactionId, occasionType) => {
    try {
      // Track the completed payment event
      mixpanel.track('Payment Completed', {
        amount,
        payment_type: paymentType,
        transaction_id: transactionId,
        occasion_type: occasionType,
        funnel_step: 'payment_completed'
      });
      
      // Track revenue
      mixpanel.people.track_charge(amount, {
        payment_type: paymentType,
        transaction_id: transactionId,
        occasion_type: occasionType
      });
      
      // Update funnel stage property
      mixpanel.people.set({
        payment_funnel_stage: 'completed',
        last_payment_success: new Date().toISOString(),
        has_completed_payment: true
      });
      
      // Set event property for future segmentation
      mixpanel.people.set_once({
        first_payment_date: new Date().toISOString()
      });
      
      // Increment user's total payments count
      mixpanel.people.increment({
        total_payments: 1,
        total_revenue: amount
      });
    } catch (error) {
      console.error('Mixpanel payment complete error:', error);
    }
  },
  
  trackThankYouPageView: (amount, transactionId) => {
    try {
      mixpanel.track('Thank You Page Viewed', {
        amount,
        transaction_id: transactionId,
        funnel_step: 'payment_success_page'
      });
      
      // Update funnel stage property
      mixpanel.people.set({
        payment_funnel_stage: 'completed_viewed_confirmation',
        last_funnel_completion: new Date().toISOString()
      });
    } catch (error) {
      console.error('Mixpanel thank you page error:', error);
    }
  }
};

// Track initial page view when the module loads
if (typeof window !== 'undefined') {
  // Wait for the page to fully load before tracking the initial view
  window.addEventListener('load', () => {
    // Get the current page title or path
    const pageName = document.title || window.location.pathname;
    Mixpanel.trackPageView(pageName);
  });
}

export default Mixpanel; 